import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Youtube } from 'lucide-react';
import { CHURCHES, LAND_ACKNOWLEDGMENT } from '../data/mock';
import EditableText from './EditableText';
import EditableInline from './EditableInline';

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#2a1a45] text-[#f2ead6] mt-24">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <svg viewBox="0 0 32 48" className="h-10 w-6" aria-hidden="true">
                <path d="M13 0 h6 v16 h13 v6 h-13 v26 h-6 v-26 h-13 v-6 h13 z" fill="#e6d4a8" />
              </svg>
              <span className="font-display text-2xl leading-tight">All Saints<br/>Catholic Parish</span>
            </div>
            <p className="text-sm text-[#d5c9a5] leading-relaxed">
              A Catholic parish serving Ladysmith and Chemainus on Vancouver Island. Two churches, one faith community.
            </p>
          </div>

          {/* Church locations */}
          {CHURCHES.map(c => (
            <div key={c.id}>
              <h4 className="font-display text-xl text-[#e6d4a8] mb-4">{c.name}</h4>
              <p className="text-sm text-[#d5c9a5] mb-3">{c.community}</p>
              <div className="space-y-2 text-sm text-[#d5c9a5]">
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0 text-[#e6d4a8]" />
                  <EditableInline
                    contentKey={`church_${c.id}_address`}
                    defaultValue={c.address}
                    label={`${c.name} address`}
                    placeholder="Street, City, Province Postal"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} className="flex-shrink-0 text-[#e6d4a8]" />
                  <EditableInline
                    contentKey={`church_${c.id}_phone`}
                    defaultValue={c.phone}
                    label={`${c.name} phone`}
                    placeholder="(250) 555-0000"
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-xl text-[#e6d4a8] mb-4">Parish Office</h4>
            <div className="space-y-2 text-sm text-[#d5c9a5] mb-6">
              <div className="flex items-center gap-2">
                <Mail size={16} className="flex-shrink-0 text-[#e6d4a8]" />
                <EditableInline
                  as="a"
                  href="mailto:auto"
                  contentKey="office_email"
                  defaultValue="allsaintsparish@rcdvictoria.org"
                  label="Parish office email"
                  className="hover:text-white break-all"
                  placeholder="office@example.com"
                />
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} className="flex-shrink-0 text-[#e6d4a8]" />
                <EditableInline
                  as="a"
                  href="tel:auto"
                  contentKey="office_phone"
                  defaultValue="(250) 245-3714"
                  label="Parish office phone"
                  className="hover:text-white"
                  placeholder="(250) 555-0000"
                />
              </div>
              <div className="pt-1">
                <p className="text-xs text-[#c0b58f] font-medium">Office Hours</p>
                <EditableInline
                  contentKey="office_hours"
                  defaultValue="Tues – Fri, 9:00 AM – 3:00 PM"
                  label="Parish office hours"
                  className="text-xs text-[#c0b58f] mt-0.5"
                  placeholder="Mon – Fri, 9 AM – 4 PM"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" aria-label="Facebook" className="text-[#e6d4a8] hover:text-white"><Facebook size={20} /></a>
              <a href="#" aria-label="YouTube" className="text-[#e6d4a8] hover:text-white"><Youtube size={20} /></a>
            </div>
          </div>
        </div>

        {/* Land Acknowledgment */}
        <div className="mt-14 pt-8 border-t border-[#4a3466]">
          <h5 className="font-display text-lg text-[#e6d4a8] mb-3">Land Acknowledgment</h5>
          <EditableText
            contentKey="footer_land_acknowledgment"
            label="Footer Land Acknowledgment"
            rows={6}
            defaultValue={LAND_ACKNOWLEDGMENT}
            paragraphClass="text-sm text-[#c0b58f] leading-relaxed max-w-4xl"
          />
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-[#4a3466] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#c0b58f]">
          <p>
            &copy; {year}{' '}
            <EditableInline
              contentKey="footer_copyright"
              defaultValue="All Saints Catholic Parish. Archdiocese of Vancouver."
              label="Footer copyright line"
              placeholder="Your Parish Name. Archdiocese."
            />
          </p>
          <div className="flex gap-5">
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/prayer-request" className="hover:text-white">Prayer Request</Link>
            <Link to="/new-parishioner" className="hover:text-white">Register</Link>
            <Link to="/give" className="hover:text-white">Give</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
